import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, FormControlLabel, Switch, Typography } from '@mui/material';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import { useTranslation } from 'react-i18next';
import { resetDraftTutorial } from '../../Utils/onboardingPreferences';
import { useComponentWebSocket } from './GlobalSettingUtils';

const AppPreferencesSettings = () => {
  const { t } = useTranslation();
  const [resetComplete, setResetComplete] = useState(false);
  const [assistantEnabled, setAssistantEnabled] = useState(false);
  const [canManageAssistant, setCanManageAssistant] = useState(false);
  const [savingAssistant, setSavingAssistant] = useState(false);
  const [assistantStatus, setAssistantStatus] = useState('');
  const pendingAssistant = useRef(null);
  const unsavedAssistant = useRef(false);
  const { sendCommand } = useComponentWebSocket({
    configuration_data: (message) => {
      setCanManageAssistant(message.data?.canManageProjectAssistant === true);
      if (!unsavedAssistant.current) {
        setAssistantEnabled(message.data?.project_assistant_enabled === true);
      }
    },
    update_configuration: (message) => {
      if (pendingAssistant.current === null) return;
      const savedValue = pendingAssistant.current;
      pendingAssistant.current = null;
      unsavedAssistant.current = message.data !== 'success';
      setSavingAssistant(false);
      setAssistantStatus(message.data === 'success' ? (savedValue ? 'enabled' : 'disabled') : 'error');
    },
    projectAssistantAvailabilityChanged: () => {
      sendCommand('configuration_check');
    },
  });

  useEffect(() => {
    sendCommand('configuration_check');
  }, [sendCommand]);

  const saveAssistant = (enabled) => {
    if (pendingAssistant.current !== null) return;
    setAssistantEnabled(enabled);
    setSavingAssistant(true);
    setAssistantStatus('');
    pendingAssistant.current = enabled;
    unsavedAssistant.current = true;
    sendCommand('update_configuration', { project_assistant_enabled: enabled });
  };

  const handleResetDraftTutorial = () => {
    setResetComplete(resetDraftTutorial());
  };

  return (
    <Box data-testid="app-preferences-settings">
      <Typography variant="h5" sx={{ color: '#5757b0', fontWeight: 700, mb: 0.75 }}>
        {t('global_setting.experience_title', 'Experience')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('global_setting.experience_description', 'Manage the assistant and guidance available while working on projects.')}
      </Typography>

      {canManageAssistant && (
        <Box sx={{ p: { xs: 2, sm: 2.5 }, mb: 2, border: '1px solid rgba(87, 87, 176, 0.16)', borderRadius: 2 }}>
          <FormControlLabel
            sx={{ ml: 0, gap: 1, justifyContent: 'space-between', width: '100%' }}
            labelPlacement="start"
            label={<Typography variant="subtitle1" sx={{ color: '#2d3748', fontWeight: 700 }}>
              {t('global_setting.experience_assistant_title', 'Project assistant')}
            </Typography>}
            control={<Switch checked={assistantEnabled} disabled={savingAssistant}
              onChange={(event) => saveAssistant(event.target.checked)}
              inputProps={{ 'aria-describedby': 'project-assistant-description' }} />}
          />
          <Typography id="project-assistant-description" variant="body2" color="text.secondary">
            {t('global_setting.experience_assistant_description', 'Let users chat with the assistant in their projects. Off by default. Changes apply to everyone immediately, without restarting the app.')}
          </Typography>
          {savingAssistant && <Typography role="status" variant="body2" sx={{ mt: 1 }}>
            {t('global_setting.experience_assistant_saving', 'Saving…')}
          </Typography>}
          {assistantStatus && <Alert severity={assistantStatus === 'error' ? 'error' : 'success'} sx={{ mt: 2 }}
            action={assistantStatus === 'error' ? <Button color="inherit" onClick={() => saveAssistant(assistantEnabled)}>
              {t('global_setting.experience_assistant_retry', 'Try again')}
            </Button> : undefined}>
            {t(`global_setting.experience_assistant_${assistantStatus}`, {
              enabled: 'The assistant is now available in projects.',
              disabled: 'The assistant is now turned off.',
              error: 'Could not save this change. Your selection is kept. Try again.',
            }[assistantStatus])}
          </Alert>}
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          p: { xs: 2, sm: 2.5 },
          border: '1px solid rgba(87, 87, 176, 0.16)',
          borderRadius: 2,
          background: 'linear-gradient(135deg, #fbfbff 0%, #f3f3ff 100%)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 46,
            height: 46,
            borderRadius: 2,
            color: '#fff',
            background: 'linear-gradient(135deg, #5757b0 0%, #7777d0 100%)',
            boxShadow: '0 4px 15px rgba(87, 87, 176, 0.25)',
            flexShrink: 0,
          }}
        >
          <TipsAndUpdatesRoundedIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ color: '#2d3748', fontWeight: 700 }}>
            {t('global_setting.experience_flow_tutorial_title', 'Empty flow tutorial')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(
              'global_setting.experience_flow_tutorial_description',
              'Show the quick guide for adding a connector on the left and the first action on the right of a new flow.'
            )}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ReplayRoundedIcon />}
          onClick={handleResetDraftTutorial}
          sx={{
            color: '#5757b0',
            borderColor: '#5757b0',
            whiteSpace: 'nowrap',
            '&:hover': {
              borderColor: '#4338ca',
              backgroundColor: 'rgba(87, 87, 176, 0.06)',
            },
          }}
        >
          {t('global_setting.experience_flow_tutorial_reset', 'Show tutorial again')}
        </Button>
      </Box>

      {resetComplete && (
        <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
          {t(
            'global_setting.experience_flow_tutorial_reset_success',
            'The flow tutorial will appear again the next time you open a new empty flow.'
          )}
        </Alert>
      )}
    </Box>
  );
};

export default AppPreferencesSettings;
